(() => {
  "use strict";
  if (window.__faithProductLoaded) return;
  window.__faithProductLoaded = true;

  const controllers = new WeakMap();

  const initSection = (section) => {
    if (controllers.has(section)) return;
    const select = section.querySelector("[data-product-variant]");
    const dataNode = section.querySelector("[data-product-variants]");
    if (!select || !dataNode) {
      controllers.set(section, { destroy() {} });
      return;
    }
    const status = section.querySelector("[data-product-status]");
    const submit = section.querySelector("[data-product-submit]");
    let variants;
    try {
      variants = JSON.parse(dataNode.textContent || "[]");
      if (!Array.isArray(variants) || !variants.length) throw new Error("Missing variant data");
    } catch {
      if (status) status.textContent = "Product options could not be loaded. Please reload this page.";
      if (submit) submit.disabled = true;
      controllers.set(section, { destroy() {} });
      return;
    }
    const price = section.querySelector("[data-product-price]");
    const compare = section.querySelector("[data-product-compare]");
    const availability = section.querySelector("[data-product-availability]");
    const form = section.querySelector("[data-product-form], .product-form, form");
    const picker = section.querySelector("[data-product-options]");
    const groups = Array.from(section.querySelectorAll("[data-option-position]"));
    const fallback = section.querySelector("[data-product-variant-fallback]");
    const primary = section.querySelector("[data-product-primary] .product-image-frame");
    let currentVariant = variants.find((item) => String(item.id) === select.value);
    let selectedOptions = currentVariant?.options?.slice() || [];

    const updateImage = (variant) => {
      if (!primary) return;
      let image = primary.querySelector("img");
      let empty = primary.querySelector(".product-image-frame__empty");
      if (variant?.image) {
        if (!image) {
          image = document.createElement("img");
          image.className = "product-image-frame__image";
          primary.append(image);
        }
        image.src = variant.image;
        // Do not leave a previous variant's responsive image candidates.
        image.removeAttribute("srcset");
        image.alt = variant.imageAlt || variant.title || "";
        image.hidden = false;
        if (empty) empty.hidden = true;
      } else {
        if (image) image.hidden = true;
        if (!empty) {
          empty = document.createElement("span");
          empty.className = "product-image-frame__empty";
          empty.textContent = "Image coming soon";
          primary.append(empty);
        }
        empty.hidden = false;
      }
    };

    const update = (variant, updateUrl = true) => {
      currentVariant = variant;
      if (price) price.textContent = variant?.price || "Unavailable";
      if (compare) {
        compare.textContent = variant?.compare || "";
        compare.hidden = !variant?.compare;
      }
      if (availability) availability.textContent = variant ? (variant.available ? "Available" : "Sold out") : "Unavailable combination";
      if (submit) {
        submit.disabled = !variant?.available;
        submit.textContent = !variant ? "Unavailable combination" : variant.available ? "Add to cart" : "Sold out";
      }
      if (status) status.textContent = variant
        ? `${variant.title}: ${variant.price}${variant.available ? "" : ", sold out"}`
        : "This combination is not available. Choose another color or size.";
      for (const group of groups) {
        const position = Number(group.dataset.optionPosition);
        for (const button of group.querySelectorAll("[data-option-value]")) {
          const value = button.dataset.optionValue;
          const available = variants.some((item) => item.available && Array.isArray(item.options) && item.options.every((option, index) => option === (index === position ? value : selectedOptions[index])));
          button.setAttribute("aria-pressed", String(selectedOptions[position] === value));
          button.dataset.optionAvailable = String(available);
          button.setAttribute("aria-label", `${value}${available ? "" : " — unavailable with the other selected options"}`);
        }
      }
      updateImage(variant);
      if (!updateUrl) return;
      try {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set("variant", String(variant.id));
        else url.searchParams.delete("variant");
        window.history.replaceState(window.history.state, "", url.toString());
      } catch {
        /* history unavailable */
      }
    };

    const onSelect = () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      selectedOptions = variant?.options?.slice() || [];
      update(variant);
    };
    const onOption = (event) => {
      const button = event.target.closest?.("[data-option-value]");
      if (!button || !picker?.contains(button)) return;
      const position = Number(button.closest("[data-option-position]").dataset.optionPosition);
      selectedOptions[position] = button.dataset.optionValue;
      const variant = variants.find((item) => Array.isArray(item.options) && item.options.length === selectedOptions.length && item.options.every((value, index) => value === selectedOptions[index]));
      select.value = variant ? String(variant.id) : "";
      update(variant);
    };
    const onSubmit = (event) => {
      if (!currentVariant?.available) event.preventDefault();
    };
    try {
      const requested = new URL(window.location.href).searchParams.get("variant");
      const variant = variants.find((item) => String(item.id) === requested);
      if (variant) {
        select.value = String(variant.id);
        currentVariant = variant;
        selectedOptions = variant.options?.slice() || [];
      }
    } catch { /* Shopify's server-selected variant remains authoritative. */ }
    if (picker && groups.length && selectedOptions.length === groups.length) {
      picker.hidden = false;
      if (fallback) fallback.hidden = true;
    }
    update(currentVariant, false);
    select.addEventListener("change", onSelect);
    picker?.addEventListener("click", onOption);
    form?.addEventListener("submit", onSubmit);
    controllers.set(section, { destroy() {
      select.removeEventListener("change", onSelect);
      picker?.removeEventListener("click", onOption);
      form?.removeEventListener("submit", onSubmit);
    } });
  };

  const init = (root) => {
    const sections = [];
    if (root instanceof Element && root.matches("[data-product-section]")) sections.push(root);
    root.querySelectorAll("[data-product-section]").forEach((node) => sections.push(node));
    sections.forEach(initSection);
  };

  const destroy = (root) => {
    const sections = [];
    if (root instanceof Element && root.matches("[data-product-section]")) sections.push(root);
    root.querySelectorAll("[data-product-section]").forEach((node) => sections.push(node));
    sections.forEach((node) => {
      controllers.get(node)?.destroy();
      controllers.delete(node);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));
})();
